import React, { useState, useMemo, useCallback } from 'react';
import { Container, Row, Col, Card, Button, Badge, Form } from 'react-bootstrap';
import { motion } from 'framer-motion';
import { useLanguage } from '../../../contexts/LanguageContext'; // Assure-toi que le dossier est 'context' ou 'Context'
import { 
  FaExchangeAlt, FaServer, FaCheckCircle, FaPhone, FaExternalLinkAlt, 
  FaMicrochip, FaMemory, FaHdd, FaRocket, FaNetworkWired, FaCloud, 
  FaFilter, FaSearch, FaTh, FaList, FaChevronLeft, FaChevronRight, 
  FaLock, FaGlobe, FaShieldAlt, FaBalanceScale
} from 'react-icons/fa';
import { useScrollAnimation } from '../../../hooks';
import { Helmet } from 'react-helmet-async';

const LoadBalancer = () => {
  const { t } = useLanguage();

  const LOAD_BALANCER_DATA = useMemo(() => ({
    categories: [
      { id: 'hardware-appliances', name: t.loadbalancer.categories.hardware.name, description: t.loadbalancer.categories.hardware.desc, icon: <FaServer /> },
      { id: 'virtual-machines', name: t.loadbalancer.categories.virtual.name, description: t.loadbalancer.categories.virtual.desc, icon: <FaCloud /> },
      { id: 'global-server-lb', name: t.loadbalancer.categories.gslb.name, description: t.loadbalancer.categories.gslb.desc, icon: <FaBalanceScale /> }
    ],
    solutions: [
      { id: 'fortiadc-120f', name: 'FortiADC 120F', category: 'hardware-appliances', badge: t.loadbalancer.products.f120f.badge, isNew: false, image: '/images/LoadBalancer/fortiadc-120f.png', imageSize: { width: '105%', height: '100%', objectFit: 'contain' }, imagePosition: { objectPosition: 'center center' }, workloads: t.loadbalancer.products.f120f.workloads, specs: { processor: t.loadbalancer.products.f120f.specs.cpu, memory: t.loadbalancer.products.f120f.specs.perf, storage: t.loadbalancer.products.f120f.specs.int, network: t.loadbalancer.products.f120f.specs.net }, features: t.loadbalancer.products.f120f.features },
      { id: 'fortiadc-220f', name: 'FortiADC 220F', category: 'hardware-appliances', badge: t.loadbalancer.products.f220f.badge, isNew: false, image: '/images/LoadBalancer/fortiadc-220f.png', imageSize: { width: '104%', height: '100%', objectFit: 'contain' }, imagePosition: { objectPosition: 'center center' }, workloads: t.loadbalancer.products.f220f.workloads, specs: { processor: t.loadbalancer.products.f220f.specs.cpu, memory: t.loadbalancer.products.f220f.specs.perf, storage: t.loadbalancer.products.f220f.specs.int, network: t.loadbalancer.products.f220f.specs.net }, features: t.loadbalancer.products.f220f.features },
      { id: 'fortiadc-300f', name: 'FortiADC 300F', category: 'hardware-appliances', badge: t.loadbalancer.products.f300f.badge, isNew: false, image: '/images/LoadBalancer/fortiadc-300f.png', imageSize: { width: '100%', height: '100%', objectFit: 'contain' }, imagePosition: { objectPosition: 'center center' }, workloads: t.loadbalancer.products.f300f.workloads, specs: { processor: t.loadbalancer.products.f300f.specs.cpu, memory: t.loadbalancer.products.f300f.specs.perf, storage: t.loadbalancer.products.f300f.specs.int, network: t.loadbalancer.products.f300f.specs.net }, features: t.loadbalancer.products.f300f.features },
      { id: 'fortiadc-400f', name: 'FortiADC 400F', category: 'hardware-appliances', badge: t.loadbalancer.products.f400f.badge, isNew: false, image: '/images/LoadBalancer/fortiadc-400f.png', imageSize: { width: '100%', height: '100%', objectFit: 'contain' }, imagePosition: { objectPosition: 'center center' }, workloads: t.loadbalancer.products.f400f.workloads, specs: { processor: t.loadbalancer.products.f400f.specs.cpu, memory: t.loadbalancer.products.f400f.specs.perf, storage: t.loadbalancer.products.f400f.specs.int, network: t.loadbalancer.products.f400f.specs.net }, features: t.loadbalancer.products.f400f.features },
      { id: 'fortiadc-vm', name: 'FortiADC VM', category: 'virtual-machines', badge: t.loadbalancer.products.fvm.badge, isNew: false, image: '/images/LoadBalancer/fortiadc-vm.jpg', imageSize: { width: '100%', height: '100%', objectFit: 'contain' }, imagePosition: { objectPosition: 'center center' }, workloads: t.loadbalancer.products.fvm.workloads, specs: { processor: t.loadbalancer.products.fvm.specs.cpu, memory: t.loadbalancer.products.fvm.specs.perf, storage: t.loadbalancer.products.fvm.specs.int, network: t.loadbalancer.products.fvm.specs.net }, features: t.loadbalancer.products.fvm.features },
      { id: 'fortigslb-cloud', name: 'FortiGSLB Cloud', category: 'global-server-lb', badge: t.loadbalancer.products.fgslb.badge, isNew: true, image: '/images/LoadBalancer/fortigslb-cloud.png', imageSize: { width: '100%', height: '100%', objectFit: 'contain' }, imagePosition: { objectPosition: 'center center' }, workloads: t.loadbalancer.products.fgslb.workloads, specs: { processor: t.loadbalancer.products.fgslb.specs.cpu, memory: t.loadbalancer.products.fgslb.specs.perf, storage: t.loadbalancer.products.fgslb.specs.int, network: t.loadbalancer.products.fgslb.specs.net }, features: t.loadbalancer.products.fgslb.features }
    ]
  }), [t]);

  const [setHeroRef, heroInView] = useScrollAnimation(0.1);
  const [setCategoriesRef, categoriesInView] = useScrollAnimation(0.1);
  const [setSolutionsRef, solutionsInView] = useScrollAnimation(0.1);

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 4;

  const filteredSolutions = useMemo(() => {
    return LOAD_BALANCER_DATA.solutions.filter(solution => {
      const matchCategory = selectedCategory === 'all' || solution.category === selectedCategory;
      const matchSearch = solution.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         solution.features.some(feature => feature.toLowerCase().includes(searchTerm.toLowerCase())) ||
                         solution.workloads.some(workload => workload.toLowerCase().includes(searchTerm.toLowerCase()));
      return matchCategory && matchSearch;
    });
  }, [selectedCategory, searchTerm, LOAD_BALANCER_DATA.solutions]);

  const paginatedSolutions = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredSolutions.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredSolutions, currentPage]);

  const totalPages = Math.ceil(filteredSolutions.length / itemsPerPage);

  const handleCategoryChange = useCallback((category) => {
    setSelectedCategory(category);
    setCurrentPage(1);
  }, []);

  const handleSearchChange = useCallback((e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  }, []);

  const scrollToSolutions = () => {
    document.getElementById('solutions-section').scrollIntoView({ behavior: 'smooth' });
  };

  const animationVariants = {
    fadeInUp: { initial: { opacity: 0, y: 30 }, animate: { opacity: 1, y: 0 }, transition: { duration: 0.8 } },
    scaleIn: { initial: { opacity: 0, scale: 0.8 }, animate: { opacity: 1, scale: 1 }, transition: { duration: 0.6 } }
  };

  return (
    <div>
      {/* Section Hero */}
      {/* --- SEO DYNAMIQUE POUR GOOGLE --- */}
      <Helmet>
        <title>{t.loadbalancer.seo.title}</title>
        <meta name="description" content={t.loadbalancer.seo.description} />
        <link rel="canonical" href="https://www.s2i.com.tn/solutions/load-balancer" />
        
        {/* Open Graph (Social Media) */}
        <meta property="og:title" content={t.loadbalancer.seo.title} />
        <meta property="og:description" content={t.loadbalancer.seo.description} />
        <meta property="og:image" content="https://www.s2i.com.tn/images/LoadBalancer/fortiadc-300f.png" />
      </Helmet>
      {/* ---------------------------------- */}

 
      <section 
        ref={setHeroRef}
        className="hero-section-sii" 
        style={{ 
          background: `linear-gradient(135deg, rgba(30, 41, 59, 0.9) 0%, rgba(71, 85, 105, 0.9) 100%), url('/images/loadbalancer-background.webp')`,
          backgroundSize: 'cover', backgroundPosition: 'center', backgroundAttachment: 'fixed',
          minHeight: '80vh', display: 'flex', alignItems: 'center', position: 'relative', overflow: 'hidden'
        }}
      >
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'4\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")', opacity: 0.3 }} />

        <Container style={{ position: 'relative', zIndex: 1 }}>
          <Row className="align-items-center">
            <Col lg={6}>
              <motion.div initial={{ opacity: 0, y: 50 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8 }}>
                <motion.div {...animationVariants.scaleIn} transition={{ delay: 0.2 }} className="mb-4">
                  <span className="badge-sii">
                    <FaExchangeAlt className="me-2" />
                    {t.loadbalancer.hero.badge}
                  </span>
                </motion.div>

                <motion.h1 
                  className="display-3 fw-bold mb-4 text-white"
                  dangerouslySetInnerHTML={{ __html: t.loadbalancer.hero.title }}
                  style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: '1.1', fontWeight: '700' }}
                />

                <motion.p className="lead text-light mb-4" style={{ fontSize: 'clamp(1rem, 2.5vw, 1.25rem)', maxWidth: '600px', lineHeight: '1.6', color: 'rgba(255,255,255,0.9)' }}>
                  {t.loadbalancer.hero.subtitle}
                </motion.p>

                <motion.div className="d-flex flex-wrap gap-3 mb-5" initial={{ opacity: 0, y: 30 }} animate={heroInView ? { opacity: 1, y: 0 } : {}} transition={{ duration: 0.8, delay: 0.7 }}>
                  <Button size="lg" onClick={scrollToSolutions} style={{ borderRadius: '16px', padding: '1rem 2.5rem', fontWeight: 600, fontSize: '1rem', background: '#d90000', color: 'white', border: 'none', boxShadow: '0 4px 16px rgba(217, 0, 0, 0.3)', transition: 'all 0.3s ease' }}>
                    <FaRocket className="me-2" /> {t.loadbalancer.hero.btnExplore}
                  </Button>
                  <Button variant="outline-light" size="lg" href="tel:+33123456789" style={{ borderRadius: '16px', padding: '1rem 2.5rem', fontWeight: 600, fontSize: '1rem', borderWidth: '2px', background: 'rgba(255,255,255,0.1)', backdropFilter: 'blur(10px)' }}>
                    <FaPhone className="me-2" /> {t.loadbalancer.hero.btnContact}
                  </Button>
                </motion.div>
              </motion.div>
            </Col>

            <Col lg={6}>
              <motion.div initial={{ opacity: 0, x: 50 }} animate={heroInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.8, delay: 0.4 }} className="text-center">
                <div style={{ background: 'rgba(255, 255, 255, 0.1)', borderRadius: '24px', padding: '3rem', backdropFilter: 'blur(20px)', border: '1px solid rgba(255, 255, 255, 0.2)', boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3)' }}>
                  <h3 className="text-white mb-4 fw-bold">{t.loadbalancer.hero.whyTitle}</h3>
                  <div className="text-start">
                    {t.loadbalancer.hero.whyList.map((feature, index) => (
                      <motion.div key={index} className="d-flex align-items-center mb-3" initial={{ opacity: 0, x: -20 }} animate={heroInView ? { opacity: 1, x: 0 } : {}} transition={{ duration: 0.5, delay: 0.9 + (index * 0.1) }}>
                        <FaCheckCircle className="me-3 text-success" style={{ minWidth: '20px' }} />
                        <span className="text-white">{feature}</span>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Section Catégories */}
      <section className="categories-section py-5 bg-light">
        <Container fluid>
          <motion.div ref={setCategoriesRef} variants={animationVariants.fadeInUp} initial="initial" animate={categoriesInView ? "animate" : "initial"} className="text-center mb-5">
            <h2 className="fw-bold mb-3" dangerouslySetInnerHTML={{ __html: t.loadbalancer.categories.title }} />
            <p className="text-muted lead">{t.loadbalancer.categories.subtitle}</p>
          </motion.div>

          <Row className="g-4 justify-content-center">
            {LOAD_BALANCER_DATA.categories.map((category, index) => (
              <Col key={category.id} lg={3} md={6}>
                <motion.div variants={animationVariants.scaleIn} initial="initial" animate={categoriesInView ? "animate" : "initial"} transition={{ delay: index * 0.1 }}>
                  <Card className="h-100 text-center border-0 shadow-sm category-card" style={{ cursor: 'pointer' }} onClick={() => handleCategoryChange(category.id)}>
                    <Card.Body className="p-4">
                      <div className="mb-3" style={{ fontSize: '3rem', color: '#d90000' }}>{category.icon}</div>
                      <h5 className="fw-bold mb-2">{category.name}</h5>
                      <p className="text-muted small">{category.description}</p>
                    </Card.Body>
                  </Card>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* Section Solutions */}
      <section id="solutions-section" className="solutions-section py-5">
        <Container fluid>
          <motion.div ref={setSolutionsRef} variants={animationVariants.fadeInUp} initial="initial" animate={solutionsInView ? "animate" : "initial"} className="mb-5">
            <Row>
              <Col lg={3}>
                <div style={{ position: 'sticky', top: '100px', background: '#fff', borderRadius: '20px', padding: '2rem', boxShadow: '0 8px 32px rgba(0,0,0,0.1)', border: '1px solid #e9ecef', height: 'fit-content' }}>
                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '2rem', paddingBottom: '1rem', borderBottom: '2px solid #e9ecef' }}>
                    <FaFilter style={{ fontSize: '1.2rem', color: '#d90000', marginRight: '0.75rem' }} />
                    <h5 style={{ margin: 0, fontWeight: 700, color: '#1a202c', fontSize: '1.1rem' }}>{t.loadbalancer.sidebar.title}</h5>
                  </div>
                  <div className="mb-4">
                    <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.75rem', textTransform: 'uppercase', display: 'block' }}>{t.loadbalancer.sidebar.search}</label>
                    <div className="position-relative">
                      <FaSearch style={{ position: 'absolute', left: '1rem', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
                      <Form.Control type="text" placeholder={t.loadbalancer.sidebar.searchPl} value={searchTerm} onChange={handleSearchChange} style={{ borderRadius: '12px', border: '2px solid #e0e0e0', paddingLeft: '2.5rem', background: '#f8f9fa' }} />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.75rem', textTransform: 'uppercase', display: 'block' }}>{t.loadbalancer.sidebar.category}</label>
                    <div className="d-grid gap-2">
                      <Button variant={selectedCategory === 'all' ? 'danger' : 'outline-secondary'} onClick={() => handleCategoryChange('all')} style={{ borderRadius: '12px', padding: '0.75rem 1rem', fontWeight: 500, textAlign: 'left' }}>
                        <FaExchangeAlt className="me-2" /> {t.loadbalancer.sidebar.all} ({LOAD_BALANCER_DATA.solutions.length})
                      </Button>
                      {LOAD_BALANCER_DATA.categories.map(category => (
                        <Button key={category.id} variant={selectedCategory === category.id ? 'danger' : 'outline-secondary'} onClick={() => handleCategoryChange(category.id)} style={{ borderRadius: '12px', padding: '0.75rem 1rem', fontWeight: 500, textAlign: 'left' }}>
                          <span className="me-2">{category.icon}</span> {category.name}
                        </Button>
                      ))}
                    </div>
                  </div>
                  <div style={{ padding: '1rem', background: 'linear-gradient(135deg, #f8f9fa, #e9ecef)', borderRadius: '12px', border: '1px solid #dee2e6', marginBottom: '1.5rem' }}>
                    <p style={{ color: '#495057', margin: 0, fontWeight: 600, fontSize: '0.85rem', textAlign: 'center' }}>
                      <strong style={{ color: '#d90000' }}>{filteredSolutions.length}</strong> {t.loadbalancer.sidebar.results}
                    </p>
                  </div>
                  <div className="mb-3">
                    <label style={{ fontSize: '0.85rem', fontWeight: 600, color: '#4a5568', marginBottom: '0.75rem', textTransform: 'uppercase', display: 'block' }}>{t.loadbalancer.sidebar.view}</label>
                    <div className="btn-group d-grid" style={{ borderRadius: '12px', overflow: 'hidden' }}>
                      <Button variant={viewMode === 'grid' ? 'danger' : 'outline-secondary'} onClick={() => setViewMode('grid')} style={{ border: 'none' }}><FaTh className="me-2" />{t.loadbalancer.sidebar.grid}</Button>
                      <Button variant={viewMode === 'list' ? 'danger' : 'outline-secondary'} onClick={() => setViewMode('list')} style={{ border: 'none' }}><FaList className="me-2" />{t.loadbalancer.sidebar.list}</Button>
                    </div>
                  </div>
                </div>
              </Col>

              <Col lg={9}>
                {/* ... (Partie Grid/List reste identique structurellement avec les traductions t.loadbalancer.card...) */}
                <div className="d-flex justify-content-between align-items-center mb-4">
                  <div>
                    <h3 className="fw-bold mb-1">Load Balancers <span style={{ color: '#d90000' }}>FortiADC</span></h3>
                    <p className="text-muted mb-0">{filteredSolutions.length} {t.loadbalancer.sidebar.results}</p>
                  </div>
                </div>

                {viewMode === 'grid' ? (
                  <Row className="g-4 mb-4">
                    {paginatedSolutions.map((solution, index) => (
                      <Col key={solution.id} lg={6} md={6}>
                        <Card className="h-100 shadow-sm border-0 solution-card">
                          <div className="position-relative overflow-hidden" style={{ height: '220px', background: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)' }}>
                            <div className="position-absolute top-0 end-0 p-3 d-flex flex-column gap-2" style={{ zIndex: 2 }}>
                              {solution.isNew && <Badge bg="success"><small>Nouveau</small></Badge>}
                              {solution.badge && <Badge bg="danger"><small>{solution.badge}</small></Badge>}
                            </div>
                            <img src={solution.image} alt={solution.name} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                          </div>
                          <Card.Body className="d-flex flex-column">
                            <h5 className="fw-bold mb-2">{solution.name}</h5>
                            <div className="d-flex flex-wrap gap-1 mb-3">
                              {solution.workloads.map((workload, idx) => <Badge key={idx} bg="light" text="dark" className="small">{workload}</Badge>)}
                            </div>
                            <div className="specs-grid mb-3">
                              <Row className="g-2">
                                <Col xs={6}><div className="d-flex align-items-center gap-2"><FaMicrochip className="text-danger" /><div><small className="text-muted">Processeur</small><div className="small fw-semibold">{solution.specs.processor}</div></div></div></Col>
                                <Col xs={6}><div className="d-flex align-items-center gap-2"><FaMemory className="text-danger" /><div><small className="text-muted">Performance</small><div className="small fw-semibold">{solution.specs.memory}</div></div></div></Col>
                                <Col xs={6}><div className="d-flex align-items-center gap-2"><FaHdd className="text-danger" /><div><small className="text-muted">Interfaces</small><div className="small fw-semibold">{solution.specs.storage}</div></div></div></Col>
                                <Col xs={6}><div className="d-flex align-items-center gap-2"><FaNetworkWired className="text-danger" /><div><small className="text-muted">Réseau</small><div className="small fw-semibold">{solution.specs.network}</div></div></div></Col>
                              </Row>
                            </div>
                          </Card.Body>
                        </Card>
                      </Col>
                    ))}
                  </Row>
                ) : (
                  /* Vue Liste originale */
                  <div className="mb-4">
                    {paginatedSolutions.map((solution, index) => (
                      <Card key={solution.id} className="border-0 shadow-sm mb-3">
                        <Card.Body><Row className="align-items-center"><Col lg={2} className="text-center"><img src={solution.image} alt={solution.name} style={{ width: '100px' }} /></Col><Col lg={10}><h5 className="fw-bold">{solution.name}</h5><div className="d-flex flex-wrap gap-1 mb-2">{solution.workloads.map((w, idx) => <Badge key={idx} bg="light" text="dark" className="small">{w}</Badge>)}</div><ul className="list-unstyled d-flex gap-3 mb-0">{solution.features.slice(0, 3).map((f, idx) => <li key={idx} className="small d-flex align-items-center gap-1"><FaCheckCircle className="text-success" size={12} />{f}</li>)}</ul></Col></Row></Card.Body>
                      </Card>
                    ))}
                  </div>
                )}
              </Col>
            </Row>
          </motion.div>
        </Container>
      </section>

      {/* Section Sécurité (Celle de l'image avec les 3 piliers) */}
      <section 
        className="security-solutions-section-sii" 
        style={{ 
          padding: '8rem 0', background: `linear-gradient(135deg, rgba(30, 41, 59, 0.95) 0%, rgba(71, 85, 105, 0.95) 100%), url('/images/security-bg.jpg')`,
          backgroundSize: 'cover', backgroundAttachment: 'fixed', position: 'relative'
        }}
      >
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.03\'%3E%3Ccircle cx=\'30\' cy=\'30\' r=\'2\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")', opacity: 0.4 }} />

        <Container style={{ position: 'relative', zIndex: 1 }}>
          <div className="text-center mb-5">
            <h2 className="section-title-sii" style={{ fontSize: '2.8rem', fontWeight: 700, color: '#ffffff', marginBottom: '1.5rem' }} dangerouslySetInnerHTML={{ __html: t.loadbalancer.security.title }} />
            <p className="section-subtitle-sii" style={{ fontSize: '1.3rem', color: 'rgba(255,255,255,0.9)', maxWidth: '700px', margin: '0 auto' }}>{t.loadbalancer.security.subtitle}</p>
          </div>

          <Row className="g-4">
            {[
              { id: 'firewall', data: t.loadbalancer.security.pillars.fw, icon: FaShieldAlt, link: '/solutions/firewall' },
              { id: 'waf', data: t.loadbalancer.security.pillars.waf, icon: FaGlobe, link: '/solutions/waf' },
              { id: 'cyber', data: t.loadbalancer.security.pillars.cyber, icon: FaLock, link: '/solutions/cyber-security' }
            ].map((sol, index) => (
              <Col lg={4} md={6} key={sol.id}>
                <motion.div 
                  initial={{ opacity: 0, y: 50 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: index * 0.15 }} viewport={{ once: true }}
                  style={{ background: 'rgba(255, 255, 255, 0.95)', borderRadius: '20px', padding: '2.5rem 2rem', border: '1px solid rgba(255,255,255,0.2)', height: '100%', position: 'relative' }}
                  onClick={() => window.location.href = sol.link}
                >
                  <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '3px', background: 'linear-gradient(90deg, #d90000, rgba(217,0,0,0.6))', borderRadius: '20px 20px 0 0' }} />
                  <div style={{ background: 'linear-gradient(135deg, rgba(217,0,0,0.1), rgba(217,0,0,0.05))', borderRadius: '16px', width: '70px', height: '70px', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 2rem auto', border: '1px solid rgba(217,0,0,0.15)' }}>
                    <sol.icon style={{ fontSize: '2rem', color: '#d90000' }} />
                  </div>
                  <div className="text-center">
                    <h4 style={{ fontWeight: 700, fontSize: '1.4rem', color: '#1a202c', marginBottom: '1rem' }}>{sol.data.title}</h4>
                    <p style={{ color: '#64748b', fontSize: '0.95rem', marginBottom: '1.5rem' }}>{sol.data.desc}</p>
                    <ul className="list-unstyled mb-3 text-start">
                      {sol.data.features.map((feature, i) => (
                        <li key={i} className="d-flex align-items-start mb-2"><FaCheckCircle style={{ color: '#d90000', marginRight: '0.75rem', fontSize: '0.8rem', flexShrink: 0 }} /> <span style={{ fontSize: '0.85rem', color: '#4a5568' }}>{feature}</span></li>
                      ))}
                    </ul>
                    <div style={{ paddingTop: '1rem', borderTop: '1px solid rgba(226, 232, 240, 0.8)', color: '#d90000', fontSize: '0.9rem', fontWeight: 600 }}>
                      <span>{t.loadbalancer.security.learnMore}</span> <FaExternalLinkAlt style={{ marginLeft: '0.5rem', fontSize: '0.75rem' }} />
                    </div>
                  </div>
                </motion.div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CTA Section original */}
      <section className="cta-section-sii" style={{ padding: '6rem 0', background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)' }}>
        <Container>
          <motion.div 
            initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} viewport={{ once: true }}
            style={{ background: 'linear-gradient(135deg, #1e293b 0%, #334155 100%)', borderRadius: '24px', padding: '4rem 3rem', color: 'white', textAlign: 'center' }}
          >
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: '2rem', gap: '1rem' }}>
              {[FaExchangeAlt, FaShieldAlt, FaGlobe].map((Icon, i) => (
                <div key={i} style={{ width: '60px', height: '60px', borderRadius: '50%', background: 'rgba(217,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Icon size={24} color="#d90000" />
                </div>
              ))}
            </div>
            <h3 style={{ fontWeight: 700, marginBottom: '1.5rem', fontSize: '2.5rem' }}>{t.loadbalancer.cta.title}</h3>
            <p style={{ fontSize: '1.2rem', marginBottom: '2.5rem', opacity: 0.9, maxWidth: '800px', margin: '0 auto 2.5rem auto' }}>{t.loadbalancer.cta.subtitle}</p>
            <div className="d-flex justify-content-center gap-3 flex-wrap">
              <Button variant="light" size="lg" style={{ borderRadius: '16px', padding: '1rem 2rem', fontWeight: 600 }} onClick={() => window.location.href = '/#contact'}>
                <FaPhone className="me-2" /> {t.loadbalancer.cta.btnContact}
              </Button>
              <Button variant="outline-light" size="lg" style={{ borderRadius: '16px', padding: '1rem 2rem', fontWeight: 600, borderWidth: '2px' }}>
                <FaExternalLinkAlt className="me-2" /> {t.loadbalancer.cta.btnDemo}
              </Button>
            </div>
          </motion.div>
        </Container>
      </section>
    </div>
  );
};

export default LoadBalancer;